import { RepliesViews } from 'enums/replies/views';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import CommentRepliesManager from 'managers/topic/comment/replies';
import { EffectsContainer } from 'mixins/withEffects';
import { TopicThread } from 'models/topicThread';
import './replies.html';
import './replies.scss';

interface CommentRepliesConfig {
    comment: SubscribableOrNullableValue<TopicThread>
    commentId: SubscribableOrNullableValue<string>
    topicId: SubscribableOrNullableValue<string>
    view: SubscribableOrNullableValue<RepliesViews>
    votesPickerContainer: SubscribableOrNullableValue<HTMLElement>
    hideControls: SubscribableOrNullableValue<boolean>
    isEmpty: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class CommentRepliesVM {
    comment: Subscribable<TopicThread | undefined>;
    
    private sortedReplies: Subscribable<Array<TopicThread>>

    votesPickerContainer: SubscribableOrNullableValue<HTMLElement>
    
    replies = ko.observableArray<TopicThread>().extend({ deferred: true });
    newRepliesCountStr: Subscribable<string>

    hasNewReplies = ko.observable(false)

    areControlsHidden: Subscribable<boolean>
    isReady: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: CommentRepliesConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const view = ko.flattenComputed(config.view, 'all');
        const isEmpty = ko.flattenComputed(config.isEmpty, false);

        this.comment = ko.flattenComputed(config.comment);

        this.votesPickerContainer = config.votesPickerContainer;
        this.areControlsHidden = ko.observable(config.hideControls).unwrap().default(false);

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const user = userManager.pluck(m => m.person);
        const userId = user.pluck(u => u.personId);

        const repliesManager = storage.get(CommentRepliesManager, { topicId: config.topicId, commentId: config.commentId, isEmpty, view });
        const loadingReplies = repliesManager.pluck(m => m.loading, true);
        const allReplies = repliesManager.pluck(m => m.replies, []);

        this.sortedReplies = allReplies.sortBy(reply => reply.createDate());
        const newReplies = this.sortedReplies.difference(this.replies);

        const newRepliesCount = ko.pureComputed(() => newReplies().length);

        this.newRepliesCountStr = ko.pureComputed(() => {
            const count = newRepliesCount();
            const label = count === 1 ? labels.LoadNewReply : labels.LoadNewReplies;

            return label.replace('{0}', count.toString());
        });

        const shouldAutoSync = ko.pureComputed(() => {
            var currentNewReplies = newReplies(),
                currentUserId = userId();

            if (currentNewReplies.length > 0 && userId != undefined)
                return currentNewReplies.some(reply => reply.postedById() === currentUserId);

            return false;
        });

        this.isReady = ko.pureComputed(() => !loadingReplies()).extend({ deferred: true });

        const isVisible = ko.pureComputed(() => this.isReady() && allReplies().length > 0);

        effects.register((newReplies, shouldAutoSync) => {
            var hasNewReplies = newReplies.length > 0;

            if (hasNewReplies && shouldAutoSync)
                this.syncReplies();

            this.hasNewReplies(hasNewReplies && !shouldAutoSync);
        }, [newReplies, shouldAutoSync]);

        ko.applyBindingsToNode(info.element, {
            'visible': isVisible,

            'css': {
                'comment__replies': true
            }
        }, this);

        this.isReady.when().then(() => this.syncReplies());
    }

    syncReplies = () => {
        this.replies(_.clone(this.sortedReplies()));
        this.comment()?.replies(this.replies());
    }
}